
import React, { useState } from 'react';
import { LOCATIONS } from '../constants';

interface WarmSpaceSession {
  id: string;
  locationName: string;
  postcode: string;
  day: string; // 'Monday', 'Tuesday', etc.
  startTime: string; // '10:00'
  endTime: string; // '12:00'
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TIMES = ['10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'];

const WarmSpacesView: React.FC = () => {
  const [sessions, setSessions] = useState<WarmSpaceSession[]>([
    { id: '1', locationName: 'YMCA Orbital Community Centre', postcode: 'WD25 7QU', day: 'Friday', startTime: '10:00', endTime: '12:00' },
    { id: '2', locationName: 'YMCA Creswick Family Centre', postcode: 'AL7 4FL', day: 'Monday', startTime: '15:30', endTime: '17:00' },
    { id: '3', locationName: 'YMCA Hertford Selections Family Centre', postcode: 'SG14 2DG', day: 'Tuesday', startTime: '15:30', endTime: '17:00' },
    { id: '4', locationName: 'YMCA High Trees Family Centre', postcode: 'EN10 6AE', day: 'Wednesday', startTime: '15:30', endTime: '17:00' },
  ]);

  const handleDragStart = (e: React.DragEvent, location: any) => {
    e.dataTransfer.setData('location', JSON.stringify(location));
  };

  const handleDrop = (e: React.DragEvent, day: string, time: string) => {
    e.preventDefault();
    const locationData = e.dataTransfer.getData('location');
    if (!locationData) return;
    
    const location = JSON.parse(locationData);
    const newSession: WarmSpaceSession = {
      id: Math.random().toString(36).substr(2, 9),
      locationName: `YMCA ${location.name}`,
      postcode: 'AL1 1XX', // Mock postcode for drag-drop
      day,
      startTime: time,
      endTime: `${parseInt(time.split(':')[0]) + 1}:30` // Default 1.5 hour session
    };
    
    setSessions(prev => [...prev, newSession]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <div className="flex flex-col h-full space-y-6 md:space-y-8 pb-10">
      <div className="section-header flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="space-y-1">
          <h2 className="ymca-brand-font text-5xl md:text-6xl uppercase leading-none">Warm Spaces</h2>
          <p className="text-gray-500 font-medium uppercase text-[10px] md:text-xs tracking-[0.2em] mt-2">Manage Community Outreach Calendar</p>
        </div>
        <div className="flex gap-4 w-full md:w-auto">
          <button onClick={() => setSessions([])} className="w-full md:w-auto bg-black text-white px-6 py-3 font-bold text-[10px] uppercase tracking-widest hover:bg-ymca-orange transition-all">
            Clear Calendar
          </button>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-8 min-h-[600px]">
        {/* Drag Source: Locations Sidebar - Becomes top list on mobile */}
        <div className="w-full xl:w-72 flex flex-col space-y-4">
          <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Drag a Centre to Schedule</h3>
          <div className="flex xl:flex-col gap-3 overflow-x-auto pb-4 xl:pb-0 scrollbar-hide">
            {LOCATIONS.map(loc => (
              <div
                key={loc.id}
                draggable
                onDragStart={(e) => handleDragStart(e, loc)}
                className="flex-shrink-0 w-48 xl:w-full p-4 border-2 border-black bg-white cursor-grab hover:bg-ymca-neutral active:cursor-grabbing transition-colors group"
              >
                <p className="ymca-brand-font text-lg md:text-xl uppercase leading-none group-hover:text-ymca-orange truncate">{loc.name}</p>
                <p className="text-[8px] font-bold text-gray-400 mt-2 uppercase tracking-tighter">Regular Location</p>
              </div>
            ))}
          </div>
          <div className="hidden xl:block mt-auto p-6 bg-ymca-neutral border-l-4 border-ymca-orange text-[10px] font-bold uppercase leading-relaxed text-gray-600">
            Drag locations directly onto the calendar grid to schedule new warm space sessions.
          </div>
        </div>

        {/* The Calendar Grid: Matching the Image Aesthetic */}
        <div className="flex-1 bg-[#bc247c] p-4 md:p-10 overflow-x-auto border-2 md:border-4 border-black shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] md:shadow-[20px_20px_0px_0px_rgba(0,0,0,1)]">
          <div className="min-w-[800px]">
            {/* Headers */}
            <div className="grid grid-cols-[80px_repeat(6,1fr)]">
              <div className="h-12 md:h-16"></div>
              {DAYS.map(day => (
                <div key={day} className="h-12 md:h-16 flex items-center justify-center text-white border-l border-white/30">
                  <span className="text-sm md:text-xl font-medium">{day}</span>
                </div>
              ))}
            </div>

            {/* Grid */}
            <div className="relative border-t border-white/30">
              {TIMES.map(time => (
                <div key={time} className="grid grid-cols-[80px_repeat(6,1fr)] border-b border-white/30 group">
                  <div className="h-20 md:h-24 flex items-start justify-center text-white pt-3 md:pt-4">
                    <span className="text-lg md:text-2xl font-light">{time}</span>
                  </div>
                  {DAYS.map(day => (
                    <div 
                      key={`${day}-${time}`}
                      onDrop={(e) => handleDrop(e, day, time)}
                      onDragOver={handleDragOver}
                      className="h-20 md:h-24 border-l border-white/30 relative hover:bg-white/10 transition-colors"
                    >
                      {/* Render Sessions */}
                      {sessions
                        .filter(s => s.day === day && s.startTime === time)
                        .map(session => (
                          <div 
                            key={session.id}
                            className="absolute inset-1 md:inset-2 z-10 bg-[#f37021] p-1.5 md:p-3 text-white shadow-lg overflow-hidden flex flex-col justify-center"
                          >
                            <p className="text-[7px] md:text-[10px] font-bold leading-tight line-clamp-2 md:line-clamp-none">{session.locationName}</p>
                            <p className="hidden md:block text-[9px] font-medium mt-1">Warm Space</p>
                            <p className="text-[6px] md:text-[9px] font-bold mt-0.5 md:mt-1 tracking-tighter">{session.startTime} - {session.endTime}</p>
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarmSpacesView;
